import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {HttpErrorResponse, HttpParams} from '@angular/common/http';
import { DateFilterService } from '../../../../Services/dateFilter.service';
import {CompanyModel, CutoffCheckResponse} from '../../../../DataModels/company-model';
import { ConfirmDialogService } from '../../../../Services/confirm-dialog.service';
import { Observable } from 'rxjs';
import { CurrencyModel } from '../../../../DataModels/currency-model';
import { AccountModel } from '../../../../DataModels/account-model';
import { ResponseModel } from '../../../../DataModels/response-model';
import { IsRecordInUseService } from '../../../../Services/is-record-in-use.service';
import { BsToastService } from '../../../../Services/bs-toast-service';
import {take} from 'rxjs/operators';
// import { RoutingTrackerService} from '../../../../Services/routing-tracker.service';
import {Title} from '@angular/platform-browser';
import {NavigationService} from '../../../../Services/navigation.service';
import {CanComponentDeactivate} from '../../../../Interfaces/unsaved-changes.interface';
import {UnsavedChangesComponent} from '../../../unsaved-changes/unsaved-changes/unsaved-changes.component';


@Component({
  selector: 'app-company-form',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.css'],
  standalone: false
})
export class CompanyFormComponent implements OnInit, AfterViewInit, CanComponentDeactivate {
  @ViewChild('unsavedChanges') unsavedChanges!: UnsavedChangesComponent;
  companyID;
  protected company: CompanyModel;
  companyForm: UntypedFormGroup;
  submitted = false;
  currencies$: CurrencyModel[];
  accounts$: AccountModel[];
  logoFile: any;
  buttonsDisabled = false;
  cutoffConflicts: { batch_type: string; open_batch_count: number }[] = [];
  hasCutoffConflicts = false;

  // Unsaved-changes tracking
  originalFormData: any = {};

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  // Legacy previous-URL tracking removed; NavigationService now handles back navigation

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    public dateFilter: DateFilterService,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private isRecUsed: IsRecordInUseService,
    private titleService: Title,
    private nav: NavigationService
  ) {
    this.createForm();
    this.companyID = this.route.snapshot.params.id;

    // Previous URL is handled by NavigationService; no local routing state needed
  }

  createForm() {
    this.companyForm = this.formBuilder.group({
      id: new UntypedFormControl(''),
      name: new UntypedFormControl(''),
      short_name: new UntypedFormControl(''),
      currency_id: new UntypedFormControl(''),
      inter_company_account_id: new UntypedFormControl(''),
      year_end_date: new UntypedFormControl(''),
      cut_off_date: new UntypedFormControl(''),
      dba: new UntypedFormControl(''),
      tax_number: new UntypedFormControl(''),
      address_1: new UntypedFormControl(''),
      address_2: new UntypedFormControl(''),
      city: new UntypedFormControl(''),
      state: new UntypedFormControl(''),
      zip: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      invoice_prefix: new UntypedFormControl('inv'),
      next_invoice_number: new UntypedFormControl(1),
      next_quote_number: new UntypedFormControl(1),
      next_sales_shipment_number: new UntypedFormControl(1),
      next_purchaseorder_number: new UntypedFormControl(1),
      next_work_order_number: new UntypedFormControl(1),
      next_order_number: new UntypedFormControl(1),
      active: new UntypedFormControl(true),
      logo: new UntypedFormControl(''),
      displayLogo: new UntypedFormControl(''),
    });
  }

  setLogoFormFieldValue(file: any): void {
    // alert('event passed from upload');
    this.companyForm.patchValue({
      logo: file,
    });
    // console.log('file data uploaded at parent: ' + file);
  }

  onFileChange(event) {
    if (event.target.files.length > 0) {
      const fileData = event.target.files[0];

      const reader = new FileReader();
      reader.readAsText(fileData);

      reader.onload = () => {
        this.logoFile = reader.result;
      };
      // this.companyForm.patchValue({
      //   logo: this.logoFile
      // });
    }
  }

  ngOnInit() {
    if (this.companyID) {
      this.LoadData();
      this.loadLookups();
      this.buttonsDisabled = this.isRecUsed.isThisUsed(
        this.companyID,
        'company_id',
        't_header'
      );
      this.buttonConfig.reset = false;
    } else {
      this.loadLookups();
    }
  }

  ngAfterViewInit() {
    this.titleService.setTitle($localize`:Company @@company:Company`);
    // Establish baseline for new records after view initializes
    if (!this.companyID) {
      this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
    }
  }

  get f() {
    return this.companyForm.controls;
  }

  onDateChange(field: 'year_end_date' | 'cut_off_date', value: any): void {
    if (!value || !this.companyID) {
      return;
    }
    // ngbDatepicker returns an object {year, month, day} — convert to ISO string
    let isoDate: string;
    if (typeof value === 'object' && value.year) {
      const m = String(value.month).padStart(2, '0');
      const d = String(value.day).padStart(2, '0');
      isoDate = `${value.year}-${m}-${d}`;
    } else {
      isoDate = String(value).split('T')[0];
    }

    const params = new HttpParams()
      .set('companyId', this.companyID)
      .set('checkDate', isoDate);

    this.table.getDataByArray('company/cutoff-check', params)
      .pipe(take(1))
      .subscribe({
        next: (res: CutoffCheckResponse) => {
          if (res.has_conflicts) {
            this.hasCutoffConflicts = true;
            this.cutoffConflicts = res.conflicts;
            const lines = res.conflicts
              .map(c => `<li>${c.batch_type} — ${c.open_batch_count} open batch${c.open_batch_count !== 1 ? 'es' : ''}</li>`)
              .join('');
            this.notification.showWarningToastStayOnScreen(
              `The following batch types have open batches on or before ${isoDate}. Please Post these batches before advancing the date:<br><ul style="margin:4px 0 0 0;padding-left:1rem;">${lines}</ul>`,
              'Cutoff Date Conflict'
            );
            // Revert the field to its previous saved value
            const revert: any = {};
            revert[field] = this.company ? (this.company as any)[field] : null;
            this.companyForm.patchValue(revert);
          } else {
            this.hasCutoffConflicts = false;
            this.cutoffConflicts = [];
          }
        },
        error: () => {
          this.notification.showErrorToast(
            $localize`:Error checking cutoff date @@errorCheckingCutoffDate:Error checking cutoff date`
          );
          // Revert on error
          const revert: any = {};
          revert[field] = this.company ? (this.company as any)[field] : null;
          this.companyForm.patchValue(revert);
        }
      });
  }

  private LoadData() {
    this.table.getData('company', this.companyID).pipe(take(1))
      .subscribe({
    next: (result: any) => {
        this.company = result;
        // console.log('image loading:' + JSON.stringify(result.logo));
      },
      error: (err: HttpErrorResponse) => {
        // this.notification.showErrorToast($localize`:Error loading Companies @@errorLoadingCompanies:Error loading Companies`);
        console.log(err);
      },
      complete:  () => {
          if (this.company.year_end_date) {
            this.company.year_end_date = this.dateFilter.GetDateFormat(
              this.company.year_end_date
            );
          }
          if (this.company.cut_off_date) {
            this.company.cut_off_date = this.dateFilter.GetDateFormat(
              this.company.cut_off_date
            );
          }
          this.companyForm.patchValue(this.company);
        // Establish baseline for Unsaved Changes guard
        this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
        }
    });
  }

  loadLookups() {
    // this.companies$ = this.table.getData('company');
    this.table
      .getApiListData('account', '?pageNumber=0&pageSize=999999')
      .subscribe((res: ResponseModel) => {
        const data = res.Data as AccountModel[];
        // console.log(JSON.stringify(data));
        this.accounts$ = data;
      });
    this.table
      .getApiListData('currency', '?pageNumber=0&pageSize=999999')
      .subscribe((res: ResponseModel) => {
        const data = res.Data as CurrencyModel[];
        this.currencies$ = data;
      });
    // this.checkStocks$ =  this.table.getData('check_stock');
  }

  // Deprecated individual handlers consolidated into onSave(mode)

  deleteLogo() {
    // companyForm.controls.logo.value
    this.companyForm.patchValue({logo: ''});
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete company? @@areYouSureYouWantToDeleteCompany:Are you sure you want to delete company?`,
      this.company.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('company', this.companyID).subscribe(
          (result) => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          (error: HttpErrorResponse) => {
            // this.notification.showErrorToast($localize`:Error @@error:Error`);
            console.log(error);
          },
          () => {
            this.onClose();
          }
        );
      },
      () => {
        this.notification.showSuccessToast(
          $localize`:Delete Canceled @@deleteCanceled:Delete Canceled`
        );
      }
    );
  }

  resetForm() {
    this.companyForm.reset();
    // After an explicit reset, treat current state as clean
    this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
    if (this.unsavedChanges) {
      this.unsavedChanges.resetChanges();
    }
  }

  onClose() {
    const fallback = '/admin/company-list';
    this.nav.backOr(fallback);
  }

  onSave(mode: 'save' | 'saveClose' | 'saveNew') {
    this.submitted = true;
    if (this.companyForm.invalid) {
      this.notification.showErrorToast(
        $localize`:Form Errors! @@formErrors:Form Errors!`
      );
      return;
    }

    // Build payload without mutating the reactive form structure
    const formValue = Object.assign({}, this.companyForm.value);
    // 'displayLogo' is a view-only helper, omit from payload if present
    if (Object.prototype.hasOwnProperty.call(formValue, 'displayLogo')) {
      delete (formValue as any).displayLogo;
    }
    this.company = formValue as CompanyModel;
    if (this.companyID) {
      if (this.companyForm.invalid === false) {
        // console.log('patch company: ' + JSON.stringify(this.company));
        this.table.patch('company', this.companyID, this.company).then(
          (result) => {
            // Reset baseline to saved state
            this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
            if (this.unsavedChanges) {
              this.unsavedChanges.resetChanges();
            }
            if (mode === 'saveClose') {
              this.onClose();
            } else {
              if (mode === 'saveNew') {
                // Prepare a fresh form for a new record
                this.companyID = undefined;
                this.companyForm.reset();
                // After save-new, consider the fresh form as clean
                this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
                // Remove any id from the URL completely (no id, not even id=)
                this.router.navigate(['/admin/company-form'], {
                  relativeTo: this.route,
                  queryParams: {id: null},
                  // queryParamsHandling: 'merge',
                  replaceUrl: true
                }).then(() => {
                });
                // Ensure that if the user clicks Close next, we return to the list (fallback)
                this.nav.forceNextBackToFallback();

              } else {
                // mode === 'save' -> keep values on screen
                // Ensure baseline equals current saved values
                this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
              }
            }
          },
          (error: HttpErrorResponse) => {
            //
            console.log(error);
          }
        );
        // return true;
      }
    } else {
      this.table.post('company', this.company).subscribe((res: any) => {
        // stops double posting when clicking Save
        if (mode === 'saveNew') {
          // Create complete; start a brand-new blank record
          this.companyID = undefined;
          this.companyForm.reset();
          this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
          if (this.unsavedChanges) {
            this.unsavedChanges.resetChanges();
          }
          // Ensure URL matches the New flow (no id in query params)
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {id: null},
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
          // After Save New, if user clicks Close, always go to the list (fallback) once
          this.nav.forceNextBackToFallback();
        } else if (mode === 'saveClose') {
          // Keep values, then close
          this.companyID = res.id;
          // Patch back any server-returned canonical values if needed in the future
          this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
          if (this.unsavedChanges) {
            this.unsavedChanges.resetChanges();
          }
          this.onClose();
        } else { // mode === 'save'
          // Stay on form with saved values and new ID
          this.companyID = res.id;
          this.originalFormData = JSON.parse(JSON.stringify(this.companyForm.getRawValue()));
          if (this.unsavedChanges) {
            this.unsavedChanges.resetChanges();
          }
        }
      });

      // this.notification.success($localize`:Saved @@saved:Saved`);
      // return true;
    }
    this.submitted = false;
    return true;
  }

  // Implementation for UnsavedChangesGuard
  checkForChanges(): boolean {
    try {
      const current = this.companyForm?.getRawValue?.() ?? {};
      return JSON.stringify(this.originalFormData) !== JSON.stringify(current);
    } catch {
      return false;
    }
  }
}
